/**
 * Super Admin → Public Voices CMS
 *
 * Manages the testimonial block shown on the public landing page (`#voices`).
 *
 * Edit URL: `/admin/voices`
 *
 * Capabilities:
 *   - Edit section label ("Voices")
 *   - Edit heading as colored text segments (drag-free reorder via up/down)
 *   - CRUD testimonial cards: portrait (upload OR URL), quote, name, role,
 *     enable/disable, reorder
 *   - Live preview of the public output below the editor
 */
import { useEffect, useRef, useState } from "react";
import {
    Loader2, Image as ImgIcon, Trash2, Plus, ArrowUp, ArrowDown, Eye,
    EyeOff, Upload, Save, Quote, Palette,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Field, Input, GoldButton } from "@/components/Ui";

const COLOR_OPTIONS = [
    { value: "default", label: "Default (foreground)", chip: "bg-zinc-300" },
    { value: "primary", label: "Primary (indigo)",     chip: "bg-indigo-700" },
    { value: "emerald", label: "Emerald",              chip: "bg-emerald-600" },
    { value: "rose",    label: "Rose",                 chip: "bg-rose-600" },
    { value: "amber",   label: "Amber",                chip: "bg-amber-600" },
    { value: "sky",     label: "Sky",                  chip: "bg-sky-600" },
];

const colorClass = (c) => ({
    primary: "text-primary",
    emerald: "text-emerald-600",
    rose:    "text-rose-600",
    amber:   "text-amber-600",
    sky:     "text-sky-600",
}[c] || "");

export default function VoicesConfig() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    const reload = async () => {
        try {
            const r = await api.get("/admin/voices");
            setCfg(r.data);
        } catch (e) {
            toast.error(apiError(e, "Could not load Voices config."));
        }
    };
    useEffect(() => { reload(); }, []);

    const saveHeader = async () => {
        setSaving(true);
        try {
            await api.patch("/admin/voices", {
                section_label: cfg.section_label,
                heading_segments: cfg.heading_segments,
            });
            toast.success("Saved.");
        } catch (e) {
            toast.error(apiError(e, "Save failed."));
        } finally {
            setSaving(false);
        }
    };

    if (!cfg) {
        return (
            <div className="flex items-center gap-2 text-sm text-zinc-500 py-10">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
        );
    }

    const addSegment = () => {
        setCfg({ ...cfg, heading_segments: [...cfg.heading_segments, { text: "", color: "default" }] });
    };
    const updateSegment = (i, patch) => {
        const segs = [...cfg.heading_segments];
        segs[i] = { ...segs[i], ...patch };
        setCfg({ ...cfg, heading_segments: segs });
    };
    const removeSegment = (i) => {
        setCfg({ ...cfg, heading_segments: cfg.heading_segments.filter((_, idx) => idx !== i) });
    };
    const moveSegment = (i, dir) => {
        const segs = [...cfg.heading_segments];
        const j = i + dir;
        if (j < 0 || j >= segs.length) return;
        [segs[i], segs[j]] = [segs[j], segs[i]];
        setCfg({ ...cfg, heading_segments: segs });
    };

    return (
        <div className="space-y-6" data-testid="admin-voices-page">
            <PageHeader
                title="Voices (Public Landing CMS)"
                subtitle="Edit the testimonial block shown on the public landing page at #voices. Changes go live immediately."
            />

            {/* ────── Section label + heading editor ────── */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-indigo-700 dark:text-indigo-300" />
                    <div className="text-sm font-semibold">Section label & heading</div>
                </div>
                <Field label="Section label" hint='Small text above the heading (e.g. "Voices", "Testimonials")'>
                    <Input
                        value={cfg.section_label}
                        onChange={(e) => setCfg({ ...cfg, section_label: e.target.value })}
                        maxLength={64}
                        data-testid="voices-label"
                    />
                </Field>

                <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-zinc-700 dark:text-zinc-300 mb-2">
                        Heading segments
                    </div>
                    <div className="text-[10px] text-zinc-500 mb-3">
                        Compose the headline by adding text segments with optional accent colors.
                        The original on lawos.in reads: <em>Trusted from the</em> · <em className="text-primary">bar bench</em> · <em>to the</em> · <em className="text-emerald-600">boardroom.</em>
                    </div>
                    <div className="space-y-2">
                        {cfg.heading_segments.map((seg, i) => (
                            <div key={i} className="flex items-center gap-2"
                                 data-testid={`voices-segment-${i}`}>
                                <div className="flex flex-col">
                                    <button onClick={() => moveSegment(i, -1)} disabled={i === 0}
                                            className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded disabled:opacity-30">
                                        <ArrowUp className="w-3 h-3" />
                                    </button>
                                    <button onClick={() => moveSegment(i, 1)} disabled={i === cfg.heading_segments.length - 1}
                                            className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded disabled:opacity-30">
                                        <ArrowDown className="w-3 h-3" />
                                    </button>
                                </div>
                                <Input
                                    value={seg.text}
                                    onChange={(e) => updateSegment(i, { text: e.target.value })}
                                    placeholder="Segment text…"
                                    className="flex-1"
                                />
                                <select
                                    value={seg.color}
                                    onChange={(e) => updateSegment(i, { color: e.target.value })}
                                    className="px-2 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-transparent text-sm"
                                    data-testid={`voices-segment-${i}-color`}
                                >
                                    {COLOR_OPTIONS.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </select>
                                <button onClick={() => removeSegment(i)}
                                        className="p-1.5 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded">
                                    <Trash2 className="w-4 h-4 text-rose-600" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <button onClick={addSegment}
                            className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                            data-testid="voices-add-segment">
                        <Plus className="w-3.5 h-3.5" /> Add segment
                    </button>
                </div>

                {/* Live preview of the heading */}
                <div className="border-t border-zinc-200 dark:border-zinc-800 pt-4">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Live preview</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold">
                        {cfg.section_label}
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold leading-tight mt-2">
                        {cfg.heading_segments.map((seg, i) => {
                            const cls = colorClass(seg.color);
                            return cls
                                ? <span key={i} className={cls}>{seg.text}</span>
                                : <span key={i}>{seg.text}</span>;
                        })}
                    </h2>
                </div>

                <div className="flex justify-end">
                    <GoldButton onClick={saveHeader} disabled={saving} data-testid="voices-save-header">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save heading
                    </GoldButton>
                </div>
            </div>

            {/* ────── Testimonial cards ────── */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Quote className="w-4 h-4 text-indigo-700 dark:text-indigo-300" />
                        <div className="text-sm font-semibold">Testimonial cards ({cfg.cards?.length || 0})</div>
                    </div>
                    <AddCardButton onAdded={reload} />
                </div>

                {(cfg.cards || []).sort((a,b)=>a.order-b.order).map((card, i) => (
                    <CardEditor
                        key={card.id}
                        card={card}
                        index={i}
                        total={cfg.cards.length}
                        onChanged={reload}
                    />
                ))}

                {(cfg.cards || []).length === 0 && (
                    <div className="text-center py-8 text-sm text-zinc-500">
                        No testimonials yet. Click <strong>Add testimonial</strong> above to create one.
                    </div>
                )}
            </div>
        </div>
    );
}

function AddCardButton({ onAdded }) {
    const [busy, setBusy] = useState(false);
    const add = async () => {
        setBusy(true);
        try {
            await api.post("/admin/voices/cards", {});
            toast.success("Card added.");
            onAdded?.();
        } catch (e) {
            toast.error(apiError(e, "Failed to add card."));
        } finally {
            setBusy(false);
        }
    };
    return (
        <GoldButton onClick={add} disabled={busy} data-testid="voices-add-card">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add testimonial
        </GoldButton>
    );
}

function CardEditor({ card, index, total, onChanged }) {
    const [c, setC] = useState(card);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    useEffect(() => { setC(card); }, [card]);

    const dirty = JSON.stringify(c) !== JSON.stringify(card);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/voices/cards/${card.id}`, {
                quote: c.quote, name: c.name, role: c.role,
                portrait_url: c.portrait_url, enabled: c.enabled, order: c.order,
            });
            toast.success("Saved.");
            onChanged?.();
        } catch (e) {
            toast.error(apiError(e, "Save failed."));
        } finally {
            setSaving(false);
        }
    };

    const move = async (dir) => {
        try {
            await api.patch(`/admin/voices/cards/${card.id}`,
                { order: (card.order || 0) + dir });
            onChanged?.();
        } catch (e) {
            toast.error(apiError(e, "Reorder failed."));
        }
    };

    const del = async () => {
        if (!window.confirm("Delete this testimonial?")) return;
        try {
            await api.delete(`/admin/voices/cards/${card.id}`);
            toast.success("Deleted.");
            onChanged?.();
        } catch (e) {
            toast.error(apiError(e, "Delete failed."));
        }
    };

    const toggleEnabled = async () => {
        try {
            await api.patch(`/admin/voices/cards/${card.id}`, { enabled: !c.enabled });
            onChanged?.();
        } catch (e) {
            toast.error(apiError(e, "Toggle failed."));
        }
    };

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/admin/voices/cards/${card.id}/portrait`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Portrait uploaded.");
            onChanged?.();
        } catch (e) {
            toast.error(apiError(e, "Upload failed."));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const portraitPreview = c.portrait_object_path
        ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/voices/portrait/${card.id}?v=${Date.now()}`
        : c.portrait_url;

    return (
        <div className={`border rounded-xl p-4 ${c.enabled ? "border-zinc-200 dark:border-zinc-800" : "border-dashed border-zinc-300 dark:border-zinc-700 opacity-60"}`}
             data-testid={`voices-card-${card.id}`}>
            <div className="flex items-start gap-4">
                {/* Portrait */}
                <div className="shrink-0 space-y-2">
                    <div className="w-20 h-20 rounded-xl bg-zinc-100 dark:bg-zinc-800 overflow-hidden flex items-center justify-center">
                        {portraitPreview ? (
                            <img src={portraitPreview} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <ImgIcon className="w-6 h-6 text-zinc-400" />
                        )}
                    </div>
                    <input type="file" accept="image/png,image/jpeg,image/webp" hidden ref={fileRef}
                            onChange={(e) => upload(e.target.files?.[0])} />
                    <button onClick={() => fileRef.current?.click()} disabled={uploading}
                            className="w-full text-[10px] flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-zinc-300 dark:border-zinc-700 hover:border-primary"
                            data-testid={`voices-card-${card.id}-upload`}>
                        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        Upload
                    </button>
                </div>

                {/* Fields */}
                <div className="flex-1 space-y-2">
                    <Field label="Quote">
                        <textarea
                            value={c.quote}
                            onChange={(e) => setC({ ...c, quote: e.target.value })}
                            maxLength={1000}
                            rows={2}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 dark:border-zinc-700 bg-transparent"
                            data-testid={`voices-card-${card.id}-quote`}
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Name">
                            <Input value={c.name}
                                   onChange={(e) => setC({ ...c, name: e.target.value })}
                                   maxLength={120}
                                   data-testid={`voices-card-${card.id}-name`} />
                        </Field>
                        <Field label="Role / City">
                            <Input value={c.role}
                                   onChange={(e) => setC({ ...c, role: e.target.value })}
                                   maxLength={120}
                                   data-testid={`voices-card-${card.id}-role`} />
                        </Field>
                    </div>
                    <Field label="Photo URL (used if no upload)">
                        <Input value={c.portrait_url || ""}
                               onChange={(e) => setC({ ...c, portrait_url: e.target.value })}
                               placeholder="https://…"
                               data-testid={`voices-card-${card.id}-url`} />
                    </Field>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1.5">
                    <button onClick={() => move(-1)} disabled={index === 0}
                            title="Move up"
                            className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30">
                        <ArrowUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => move(1)} disabled={index === total - 1}
                            title="Move down"
                            className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30">
                        <ArrowDown className="w-4 h-4" />
                    </button>
                    <button onClick={toggleEnabled}
                            title={c.enabled ? "Hide from public site" : "Show on public site"}
                            className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800">
                        {c.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button onClick={del} title="Delete"
                            className="p-1.5 rounded hover:bg-rose-50 dark:hover:bg-rose-900/30"
                            data-testid={`voices-card-${card.id}-delete`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                    </button>
                </div>
            </div>

            {dirty && (
                <div className="mt-2 flex justify-end">
                    <GoldButton onClick={save} disabled={saving}
                                data-testid={`voices-card-${card.id}-save`}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save changes
                    </GoldButton>
                </div>
            )}
        </div>
    );
}
